import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  IconUpload, 
  IconUser, 
  IconPhone, 
  IconSchool, 
  IconBook,
  IconCalendar,
  IconTag,
  IconAlertCircle,
  IconMap,
  IconLanguage,
  IconGenderMale,
  IconInfoCircle
} from '@tabler/icons-react';
import { userService } from '../services/api';
import styles from './UserProfileForm.module.css';
import { User, UserProfilePayload } from '../types/user';

interface UserProfileFormProps {
  userId?: number;
  isEditing?: boolean;
}

const FIELD_OF_STUDY_OPTIONS = [
  'Informatique',
  'Droit',
  'Économie',
  'Médecine',
  'Sciences sociales',
  'Arts',
  'Langues',
  'Ingénierie',
  'Business',
  'Architecture',
  'Autre'
];

const INTEREST_OPTIONS = [
  'Technologie',
  'Sport',
  'Culture',
  'Musique',
  'Art',
  'Voyage',
  'Gastronomie',
  'Cinéma',
  'Littérature',
  'Sciences',
  'Politique',
  'Environnement',
  'Mode',
  'Santé',
  'Éducation'
];

const GENDER_OPTIONS = [
  { value: 'male', label: 'Homme' },
  { value: 'female', label: 'Femme' },
  { value: 'other', label: 'Autre' },
  { value: 'prefer_not_to_say', label: 'Je préfère ne pas préciser' }
];

const LANGUAGE_OPTIONS = [
  { value: 'fr', label: 'Français' },
  { value: 'en', label: 'Anglais' },
  { value: 'es', label: 'Espagnol' },
  { value: 'de', label: 'Allemand' },
  { value: 'it', label: 'Italien' }
];

const REFERRAL_OPTIONS = [
  { value: 'friend', label: 'Ami' },
  { value: 'social_media', label: 'Réseaux sociaux' },
  { value: 'search_engine', label: 'Moteur de recherche' },
  { value: 'advertisement', label: 'Publicité' },
  { value: 'university', label: 'Université' },
  { value: 'other', label: 'Autre' }
];

const UserProfileForm: React.FC<UserProfileFormProps> = ({ 
  userId, 
  isEditing = false 
}) => {
  const navigate = useNavigate();
  
  const [formData, setFormData] = useState<UserProfilePayload>({
    username: '',
    firstName: '',
    lastName: '',
    bio: '',
    avatar: '',
    phone: '',
    university: '',
    fieldOfStudy: '',
    graduationYear: undefined,
    interests: [],
    dateOfBirth: '',
    gender: '',
    location: '',
    preferredLanguage: 'fr',
    referralSource: '',
    allowMarketing: false,
    preferences: {
      receiveNotifications: true,
      emailNotifications: true,
      pushNotifications: false,
      eventReminders: true,
      eventTypes: [],
      eventCategories: [],
      locationPreferences: [],
      darkMode: false,
      privacySettings: {
        showEmail: true,
        showPhone: false,
        showUniversity: true
      }
    }
  });
  
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState<boolean>(false);
  
  useEffect(() => {
    const fetchUserData = async () => {
      try {
        setLoading(true);
        let user: User;
        
        if (isEditing && userId) {
          user = await userService.getById(userId);
        } else {
          user = await userService.getCurrentUser();
        }
        
        setFormData({
          username: user.username || '',
          firstName: user.firstName || '',
          lastName: user.lastName || '',
          bio: user.bio || '',
          avatar: user.avatar || '',
          phone: user.phone || '',
          university: user.university || '',
          fieldOfStudy: user.fieldOfStudy || '',
          graduationYear: user.graduationYear,
          interests: user.interests || [],
          dateOfBirth: user.dateOfBirth || '',
          gender: user.gender || '',
          location: user.location || '',
          preferredLanguage: user.preferredLanguage || 'fr',
          referralSource: user.referralSource || '',
          allowMarketing: user.allowMarketing || false,
          // Les préférences seraient récupérées depuis l'API
        });
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Erreur lors du chargement des données');
      } finally {
        setLoading(false);
      }
    };

    fetchUserData();
  }, [userId, isEditing]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handlePrivacyChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      preferences: {
        ...prev.preferences!,
        privacySettings: {
          ...prev.preferences!.privacySettings,
          [name]: checked
        }
      }
    }));
  };

  const handleNotificationChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      preferences: {
        ...prev.preferences!,
        [name]: checked
      }
    }));
  };

  const handleAvatarChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      // Pour une implémentation future, on pourrait stocker le fichier et l'uploader
      console.log('Avatar file selected:', e.target.files[0].name);
    }
  };

  const handleInterestChange = (interest: string) => {
    setFormData(prev => {
      const currentInterests = prev.interests || [];
      
      if (currentInterests.includes(interest)) {
        return {
          ...prev,
          interests: currentInterests.filter(i => i !== interest)
        };
      } else {
        return {
          ...prev,
          interests: [...currentInterests, interest]
        };
      }
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);

    try {
      // Map frontend field names to backend-expected fields
      const mappedData = {
        name: `${formData.firstName || ''} ${formData.lastName || ''}`.trim(),
        dateOfBirth: formData.dateOfBirth ? new Date(formData.dateOfBirth).toISOString() : undefined,
        gender: formData.gender,
        location: formData.location,
        preferredLanguage: formData.preferredLanguage,
        referralSource: formData.referralSource,
        allowMarketing: formData.allowMarketing,
        avatar: formData.avatar,
        // Backend expects 'school' in DTO but maps it to 'university' in database
        school: formData.university
      };

      // Log complete payload for debugging
      console.log("Final sanitized payload:", mappedData);

      const response = await userService.updateProfile(mappedData);
      console.log("Update response:", response);
      
      if (formData.preferences) {
        await userService.updatePreferences(formData.preferences);
      }

      alert('Profil mis à jour avec succès');
      // Navigate to profile page or dashboard
      navigate('/profile');
    } catch (err: any) { // Type as 'any' to allow access to potential axios error properties
      console.error('Profile update error:', err);
      // Try to extract detailed error message if available
      let errorMessage = 'Une erreur est survenue';
      
      if (err?.response?.data?.message) {
        // NestJS validation error messages
        if (Array.isArray(err.response.data.message)) {
          errorMessage = err.response.data.message.join(', ');
        } else {
          errorMessage = err.response.data.message;
        }
      } else if (err instanceof Error) {
        errorMessage = err.message;
      }
      
      setError(errorMessage);
      alert(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className={styles.loadingContainer}>
        <div className={styles.loadingSpinner}></div>
        <p>Chargement des données...</p>
      </div>
    );
  }

  const currentYear = new Date().getFullYear();
  const years = Array.from(
    { length: 10 }, 
    (_, i) => currentYear + i
  );

  return (
    <div className={styles.formContainer}>
      <form onSubmit={handleSubmit}>
        <div className={styles.formSection}>
          <h2 className={styles.formTitle}>
            Informations personnelles
          </h2>
          
          {error && (
            <div className={styles.errorMessage}>
              <IconAlertCircle size={16} />
              <span>{error}</span>
            </div>
          )}
          
          <div className={styles.avatarContainer}>
            <div className={styles.avatar}>
              {formData.avatar ? (
                <img 
                  src={formData.avatar} 
                  alt="Avatar de l'utilisateur" 
                  className={styles.avatarImage}
                />
              ) : (
                <IconUser size={60} className={styles.avatarIcon} />
              )}
            </div>
            
            <div className={styles.fileInputWrapper}>
              <label htmlFor="avatar-upload" className={styles.fileInputLabel}>
                <IconUpload size={14} />
                <span>Choisir un avatar</span>
              </label>
              <input
                id="avatar-upload"
                type="file"
                accept="image/png,image/jpeg,image/svg+xml"
                onChange={handleAvatarChange}
                className={styles.fileInput}
              />
            </div>
          </div>
          
          <div className={styles.formRow}>
            <div className={styles.formField}>
              <label htmlFor="firstName" className={styles.label}>
                Prénom
              </label>
              <div className={styles.inputWrapper}>
                <IconUser size={16} className={styles.inputIcon} />
                <input
                  id="firstName"
                  type="text"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleChange}
                  placeholder="Prénom"
                  className={styles.input}
                />
              </div>
            </div>
            
            <div className={styles.formField}>
              <label htmlFor="lastName" className={styles.label}>
                Nom
              </label>
              <div className={styles.inputWrapper}>
                <IconUser size={16} className={styles.inputIcon} />
                <input
                  id="lastName"
                  type="text"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleChange}
                  placeholder="Nom"
                  className={styles.input}
                />
              </div>
            </div>
          </div>
          
          <div className={styles.formField}>
            <label htmlFor="username" className={styles.label}>
              Nom d'utilisateur<span className={styles.required}>*</span>
            </label>
            <div className={styles.inputWrapper}>
              <IconUser size={16} className={styles.inputIcon} />
              <input
                id="username"
                type="text"
                name="username"
                value={formData.username}
                onChange={handleChange}
                placeholder="Nom d'utilisateur"
                required
                className={styles.input}
              />
            </div>
          </div>
          
          <div className={styles.formField}>
            <label htmlFor="bio" className={styles.label}>
              Bio
            </label>
            <textarea
              id="bio"
              name="bio"
              value={formData.bio}
              onChange={handleChange}
              placeholder="Quelques mots sur vous..."
              className={styles.textarea}
              rows={3}
            />
          </div>
          
          <div className={styles.formRow}>
            <div className={styles.formField}>
              <label htmlFor="phone" className={styles.label}>
                Téléphone
              </label>
              <div className={styles.inputWrapper}>
                <IconPhone size={16} className={styles.inputIcon} />
                <input
                  id="phone"
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder="Numéro de téléphone"
                  className={styles.input}
                />
              </div>
            </div>
          </div>
          
          <div className={styles.formRow}>
            <div className={styles.formField}>
              <label htmlFor="dateOfBirth" className={styles.label}>
                Date de naissance
              </label>
              <div className={styles.inputWrapper}>
                <IconCalendar size={16} className={styles.inputIcon} />
                <input
                  id="dateOfBirth"
                  type="date"
                  name="dateOfBirth"
                  value={formData.dateOfBirth}
                  onChange={handleChange}
                  className={styles.input}
                />
              </div>
            </div>
            
            <div className={styles.formField}>
              <label htmlFor="gender" className={styles.label}>
                Genre
              </label>
              <div className={styles.inputWrapper}>
                <IconGenderMale size={16} className={styles.inputIcon} />
                <select
                  id="gender"
                  name="gender"
                  value={formData.gender}
                  onChange={handleChange}
                  className={styles.select}
                >
                  <option value="">Sélectionnez votre genre</option>
                  {GENDER_OPTIONS.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
          
          <div className={styles.formRow}>
            <div className={styles.formField}>
              <label htmlFor="location" className={styles.label}>
                Localisation
              </label>
              <div className={styles.inputWrapper}>
                <IconMap size={16} className={styles.inputIcon} />
                <input
                  id="location"
                  type="text"
                  name="location"
                  value={formData.location}
                  onChange={handleChange}
                  placeholder="Votre ville ou région"
                  className={styles.input}
                />
              </div>
            </div>
            
            <div className={styles.formField}>
              <label htmlFor="preferredLanguage" className={styles.label}>
                Langue préférée
              </label>
              <div className={styles.inputWrapper}>
                <IconLanguage size={16} className={styles.inputIcon} />
                <select
                  id="preferredLanguage"
                  name="preferredLanguage"
                  value={formData.preferredLanguage}
                  onChange={handleChange}
                  className={styles.select}
                >
                  {LANGUAGE_OPTIONS.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        </div>
        
        <div className={styles.formSection}>
          <h2 className={styles.formTitle}>Études</h2>
          
          <div className={styles.formField}>
            <label htmlFor="university" className={styles.label}>
              Université
            </label>
            <div className={styles.inputWrapper}>
              <IconSchool size={16} className={styles.inputIcon} />
              <input
                id="university"
                type="text"
                name="university"
                value={formData.university}
                onChange={handleChange}
                placeholder="Nom de votre université"
                className={styles.input}
              />
            </div>
          </div>
          
          <div className={styles.formRow}>
            <div className={styles.formField}>
              <label htmlFor="fieldOfStudy" className={styles.label}>
                Domaine d'études
              </label>
              <div className={styles.inputWrapper}>
                <IconBook size={16} className={styles.inputIcon} />
                <select
                  id="fieldOfStudy"
                  name="fieldOfStudy"
                  value={formData.fieldOfStudy}
                  onChange={handleChange}
                  className={styles.select}
                >
                  <option value="">Sélectionnez un domaine</option>
                  {FIELD_OF_STUDY_OPTIONS.map(option => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            
            <div className={styles.formField}>
              <label htmlFor="graduationYear" className={styles.label}>
                Année de diplôme
              </label>
              <div className={styles.inputWrapper}>
                <IconCalendar size={16} className={styles.inputIcon} />
                <select
                  id="graduationYear"
                  name="graduationYear"
                  value={formData.graduationYear || ''}
                  onChange={handleChange}
                  className={styles.select}
                >
                  <option value="">Sélectionnez une année</option>
                  {years.map(year => (
                    <option key={year} value={year}>
                      {year}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        </div>
        
        <div className={styles.formSection}>
          <h2 className={styles.formTitle}>Centres d'intérêt</h2>
          
          <div className={styles.interestsContainer}>
            {INTEREST_OPTIONS.map(interest => (
              <label key={interest} className={styles.interestOption}>
                <input
                  type="checkbox"
                  checked={formData.interests?.includes(interest) || false}
                  onChange={() => handleInterestChange(interest)}
                  className={styles.interestCheckbox}
                />
                <span className={styles.interestLabel}>
                  <IconTag size={14} />
                  {interest}
                </span>
              </label>
            ))}
          </div>
        </div>
        
        <div className={styles.formSection}>
          <h2 className={styles.formTitle}>Informations complémentaires</h2>
          
          <div className={styles.formField}>
            <label htmlFor="referralSource" className={styles.label}>
              Comment avez-vous découvert notre site ?
            </label>
            <div className={styles.inputWrapper}>
              <IconInfoCircle size={16} className={styles.inputIcon} />
              <select
                id="referralSource"
                name="referralSource"
                value={formData.referralSource}
                onChange={handleChange}
                className={styles.select}
              >
                <option value="">Sélectionnez une source</option>
                {REFERRAL_OPTIONS.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
        
        <div className={styles.formSection}>
          <h2 className={styles.formTitle}>Préférences</h2>
          
          <div className={styles.preferenceGroup}>
            <h3 className={styles.preferenceTitle}>Notifications</h3>
            
            <div className={styles.preferenceOption}>
              <label className={styles.switchLabel}>
                <input
                  type="checkbox"
                  name="receiveNotifications"
                  checked={formData.preferences?.receiveNotifications || false}
                  onChange={handleNotificationChange}
                  className={styles.switchInput}
                />
                <span className={styles.switchSlider}></span>
                Recevoir des notifications
              </label>
            </div>
            
            <div className={styles.preferenceOption}>
              <label className={styles.switchLabel}>
                <input
                  type="checkbox"
                  name="emailNotifications"
                  checked={formData.preferences?.emailNotifications || false}
                  onChange={handleNotificationChange}
                  className={styles.switchInput}
                  disabled={!formData.preferences?.receiveNotifications}
                />
                <span className={styles.switchSlider}></span>
                Notifications par email
              </label>
            </div>
            
            <div className={styles.preferenceOption}>
              <label className={styles.switchLabel}>
                <input
                  type="checkbox"
                  name="pushNotifications"
                  checked={formData.preferences?.pushNotifications || false}
                  onChange={handleNotificationChange}
                  className={styles.switchInput}
                  disabled={!formData.preferences?.receiveNotifications}
                />
                <span className={styles.switchSlider}></span>
                Notifications push
              </label>
            </div>
          </div>
          
          <div className={styles.preferenceGroup}>
            <h3 className={styles.preferenceTitle}>Communications</h3>
            
            <div className={styles.preferenceOption}>
              <label className={styles.switchLabel}>
                <input
                  type="checkbox"
                  name="allowMarketing"
                  checked={formData.allowMarketing || false}
                  onChange={(e) => setFormData(prev => ({ ...prev, allowMarketing: e.target.checked }))}
                  className={styles.switchInput}
                />
                <span className={styles.switchSlider}></span>
                Recevoir des emails marketing et offres promotionnelles
              </label>
            </div>
          </div>
          
          <div className={styles.preferenceGroup}>
            <h3 className={styles.preferenceTitle}>Confidentialité</h3>
            
            <div className={styles.preferenceOption}>
              <label className={styles.switchLabel}>
                <input
                  type="checkbox"
                  name="showEmail"
                  checked={formData.preferences?.privacySettings.showEmail || false}
                  onChange={handlePrivacyChange}
                  className={styles.switchInput}
                />
                <span className={styles.switchSlider}></span>
                Afficher mon email aux autres utilisateurs
              </label>
            </div>
            
            <div className={styles.preferenceOption}>
              <label className={styles.switchLabel}>
                <input
                  type="checkbox"
                  name="showPhone"
                  checked={formData.preferences?.privacySettings.showPhone || false}
                  onChange={handlePrivacyChange}
                  className={styles.switchInput}
                />
                <span className={styles.switchSlider}></span>
                Afficher mon téléphone aux autres utilisateurs
              </label>
            </div>
            
            <div className={styles.preferenceOption}>
              <label className={styles.switchLabel}>
                <input
                  type="checkbox"
                  name="showUniversity"
                  checked={formData.preferences?.privacySettings.showUniversity || false}
                  onChange={handlePrivacyChange}
                  className={styles.switchInput}
                />
                <span className={styles.switchSlider}></span>
                Afficher mon université aux autres utilisateurs
              </label>
            </div>
          </div>
        </div>
        
        <div className={styles.buttonGroup}>
          <button 
            type="button" 
            onClick={() => navigate(-1)}
            disabled={submitting}
            className={`${styles.button} ${styles.outlineButton}`}
          >
            Annuler
          </button>
          <button 
            type="submit" 
            disabled={submitting}
            className={styles.button}
          >
            {submitting ? 'Traitement en cours...' : 'Enregistrer'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default UserProfileForm;
